// Compact relative-or-absolute date formatting for list tables.
//
// Recent timestamps read as a short relative phrase ("Just now", "5 min ago",
// "3 hrs ago"); anything a day or more in the past collapses to a date-only
// label with no time ("Jul 8, 2026"). The 24h cutoff keeps rows scannable:
// "how long ago" matters for fresh edits, while older rows only need the date.

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const DATE_ONLY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

// "Tue Jul 8, 2026" (no comma after the weekday) — for surfaces where the
// weekday aids recall (e.g. "was that before the weekend?"), like branch
// deploy recency. Composed manually because Intl always inserts a comma
// between the weekday and the date.
const WEEKDAY_FORMATTER = new Intl.DateTimeFormat("en-US", { weekday: "short" });

/**
 * Formats a date relative to `now` when it is within the last 24 hours,
 * otherwise as a date-only string. Future dates and the last minute both read
 * as "Just now" so a small clock skew never surfaces a confusing "in 3 min".
 *
 * Pure: `now` is injectable so callers (and tests) control the reference time.
 */
export function formatRelativeOrDate(
  date: Date | null | undefined,
  now: Date = new Date(),
  options?: { withWeekday?: boolean },
): string {
  if (date == null) {
    return "-";
  }

  const deltaMs = now.getTime() - date.getTime();

  // A future timestamp (clock skew) or anything under a minute reads as "Just now".
  if (deltaMs < MINUTE_MS) {
    return "Just now";
  }

  if (deltaMs < HOUR_MS) {
    const minutes = Math.floor(deltaMs / MINUTE_MS);
    return `${minutes} min ago`;
  }

  if (deltaMs < DAY_MS) {
    const hours = Math.floor(deltaMs / HOUR_MS);
    return `${hours} ${hours === 1 ? "hr" : "hrs"} ago`;
  }

  const dateLabel = DATE_ONLY_FORMATTER.format(date);
  return options?.withWeekday === true
    ? `${WEEKDAY_FORMATTER.format(date)} ${dateLabel}`
    : dateLabel;
}
